import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api';
import { cn } from '@/shared/cn';
import MarkdownView from '@/components/MarkdownView';
import EntityGraph from '@/components/EntityGraph';
import ChatPanel from '@/components/ChatPanel';
import { useJobs } from '@/shared/jobs';
import { ArrowLeft, Loader2, FileText, Sparkles, Lock, ShieldAlert, LockOpen, ListChecks, Gavel, Check, X, GitCompareArrows, CalendarClock, MapPin, EyeOff, Download, Network, User as UserIcon, Building2, Box, Banknote, BookOpen, MessageSquareQuote, RefreshCw } from 'lucide-react';
import { APIError } from '@/shared/api';

interface Counts {
  findings: number;
  violations: number;
  inconsistencies: number;
  timeline: number;
  entities: number;
  relations: number;
  pii_total: number;
  pii_active: number;
}

type ViewKey = 'dokumen' | 'lampiran' | 'ringkasan' | 'temuan' | 'pasal' | 'kronologi' | 'kontradiksi' | 'entitas' | 'pii' | 'asisten' | 'ekspor';

const SUBNAV: { key: ViewKey; label: string; roman: string; icon: any; badgeOf: (c: Counts) => number }[] = [
  { key: 'dokumen',     label: 'Dokumen',     roman: 'I',    icon: FileText,           badgeOf: () => 0 },
  { key: 'lampiran',    label: 'Lampiran',    roman: 'II',   icon: Box,                badgeOf: () => 0 },
  { key: 'ringkasan',   label: 'Ringkasan',   roman: 'III',  icon: BookOpen,           badgeOf: () => 0 },
  { key: 'temuan',      label: 'Temuan',      roman: 'IV',   icon: ListChecks,         badgeOf: (c) => c.findings },
  { key: 'pasal',       label: 'Pasal',       roman: 'V',    icon: Gavel,              badgeOf: (c) => c.violations },
  { key: 'kronologi',   label: 'Kronologi',   roman: 'VI',   icon: CalendarClock,      badgeOf: (c) => c.timeline },
  { key: 'kontradiksi', label: 'Kontradiksi', roman: 'VII',  icon: GitCompareArrows,   badgeOf: (c) => c.inconsistencies },
  { key: 'entitas',     label: 'Entitas',     roman: 'VIII', icon: Network,            badgeOf: (c) => c.entities },
  { key: 'pii',         label: 'PII',         roman: 'IX',   icon: EyeOff,             badgeOf: (c) => c.pii_active },
  { key: 'asisten',     label: 'AI Asisten',  roman: 'X',    icon: MessageSquareQuote, badgeOf: () => 0 },
  { key: 'ekspor',      label: 'Ekspor',      roman: 'XI',   icon: Download,           badgeOf: () => 0 },
];

interface Document {
  id: string;
  bap_number: string | null;
  case_id: string | null;
  satker: string;
  classification: string;
  status: string;
  page_count: number | null;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
}

// ============ REINDEX BUTTON ============

interface ReindexResp {
  doc_id: string;
  status: string;
  task_id: string;
  queued_at: string;
}

function ReindexButton({ docId, status }: { docId: string; status: string }) {
  const qc = useQueryClient();
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const mutation = useMutation<ReindexResp, APIError>({
    mutationFn: () => api<ReindexResp>(`/api/v1/documents/${docId}/reindex`, { method: 'POST' }),
    onSuccess: () => {
      setErrMsg(null);
      // Triggers refetch — page will show "sedang diproses" banner via 3s polling
      qc.invalidateQueries({ queryKey: ['document', docId] });
      qc.invalidateQueries({ queryKey: ['counts', docId] });
    },
    onError: (err) => {
      if (err.status === 403) setErrMsg('Akses ditolak: tidak punya hak reindex BAP ini.');
      else if (err.status === 409) setErrMsg('Dokumen sedang diproses. Tunggu sampai selesai.');
      else if (err.status === 404) setErrMsg('Dokumen tidak ditemukan.');
      else setErrMsg(err.message || 'Gagal me-reindex.');
    },
  });

  const inProgress = ['PENDING', 'PROCESSING'].includes(status);
  const disabled = inProgress || mutation.isPending;

  const handleClick = () => {
    if (disabled) return;
    const ok = window.confirm(
      'Reindex akan menghapus chunks lama (Postgres + OpenSearch) lalu menjalankan ulang pipeline (extract → chunk → embed → index). Lanjutkan?',
    );
    if (!ok) return;
    mutation.mutate();
  };

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={inProgress ? 'Tidak bisa reindex saat pipeline berjalan' : 'Re-jalankan pipeline ingest untuk BAP ini'}
        className={cn(
          'inline-flex items-center gap-2 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold',
          'bg-brass text-paper border-2 border-brass shadow-sm',
          'hover:bg-brass/90 hover:shadow-md hover:-translate-y-0.5',
          'active:translate-y-0 active:shadow-sm',
          'transition-all duration-150',
          disabled && 'opacity-40 cursor-not-allowed hover:bg-brass hover:shadow-sm hover:translate-y-0',
        )}
      >
        {mutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
        Reindex
      </button>
      {errMsg && (
        <span className="font-mono text-[10px] text-crimson font-bold" role="alert">
          {errMsg}
        </span>
      )}
    </span>
  );
}

interface SummaryResp {
  bap_id: string;
  level: string;
  summary: string;
  model: string | null;
  chunks_used: number;
  tokens_in: number;
  tokens_out: number;
  citations: Array<{ chunk_id: string; page_start: number; page_end: number; section: string | null; score: number | null }>;
}

const KLAS_STYLE: Record<string, { color: string; border: string; Icon: any; label: string }> = {
  BIASA: { color: 'text-ink-2', border: 'border-ink-2', Icon: LockOpen, label: 'Biasa' },
  TERBATAS: { color: 'text-bluestamp', border: 'border-bluestamp', Icon: Lock, label: 'Terbatas' },
  RAHASIA: { color: 'text-maroon', border: 'border-maroon', Icon: ShieldAlert, label: 'Rahasia' },
  SANGAT_RAHASIA: { color: 'text-crimson', border: 'border-crimson', Icon: ShieldAlert, label: 'Sangat Rahasia' },
};

export default function BerkasDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') as ViewKey) || 'ringkasan';
  const setView = (v: ViewKey) => setSearchParams({ view: v }, { replace: true });

  const { data: doc, isLoading } = useQuery<Document>({
    queryKey: ['document', id],
    queryFn: () => api(`/api/v1/documents/${id}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const d = q.state.data as Document | undefined;
      return d && ['PENDING', 'PROCESSING'].includes(d.status) ? 3000 : false;
    },
  });

  const { data: counts } = useQuery<Counts>({
    queryKey: ['counts', id],
    queryFn: () => api(`/api/v1/documents/${id}/counts`),
    enabled: !!id && doc?.status === 'READY',
    refetchInterval: 15000,
  });

  if (isLoading) return <p className="font-mono text-sm">Memuat...</p>;
  if (!doc) return <p className="font-mono text-sm text-crimson">Berkas tidak ditemukan</p>;

  const klas = KLAS_STYLE[doc.classification] || KLAS_STYLE.BIASA;
  const ready = doc.status === 'READY';

  const safeCounts: Counts = counts || {
    findings: 0, violations: 0, inconsistencies: 0, timeline: 0,
    entities: 0, relations: 0, pii_total: 0, pii_active: 0,
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
        <aside className="subnav-side" role="tablist">
          <Link
            to="/berkas"
            className="inline-flex items-center gap-1.5 mb-2 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-2 hover:text-brass border-b border-dashed border-ink-line"
          >
            <ArrowLeft className="w-3 h-3" />
            Kembali ke daftar
          </Link>
          {SUBNAV.map((s) => {
            const Icon = s.icon;
            const active = view === s.key;
            const badge = s.badgeOf(safeCounts);
            return (
              <button
                key={s.key}
                role="tab"
                aria-selected={active}
                onClick={() => setView(s.key)}
                className={cn('subtab-side', active && 'active')}
              >
                <span className="roman">{s.roman}</span>
                <Icon />
                <span className="flex-1">{s.label}</span>
                {badge > 0 && <span className="badge">{badge}</span>}
              </button>
            );
          })}
        </aside>

        <main className="pl-8 min-w-0">
          {/* DOSSIER HEAD — classic style */}
          <div className="mb-6 pb-4 border-b-2 border-ink relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-3 mb-1">
              Berkas Perkara · {doc.case_id || 'Tanpa Case ID'} · Tahun {new Date(doc.uploaded_at).getFullYear()}
            </div>
            <h1 className="font-display font-bold text-[36px] leading-none tracking-tighter">
              {doc.bap_number || 'Tanpa Nomor LP'}
            </h1>
            <div className="font-display italic text-maroon text-[18px] mt-1.5">
              {doc.case_id ? `Kasus: ${doc.case_id}` : 'BAP'}
            </div>
            <div className="font-mono text-[11px] text-ink-2 mt-3 flex gap-3 flex-wrap items-center">
              <span>Diunggah <strong className="text-ink">{new Date(doc.uploaded_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</strong></span>
              <span className="text-ink-3">·</span>
              <span>Satker <strong className="text-ink">{doc.satker}</strong></span>
              <span className="text-ink-3">·</span>
              <span><strong className="text-ink">{doc.page_count ?? '—'}</strong> halaman</span>
              <span className="text-ink-3">·</span>
              <span className={ready ? 'text-olive font-bold' : 'text-bluestamp font-bold'}>{doc.status}</span>
              <ReindexButton docId={id!} status={doc.status} />
            </div>
            {/* Classification stamp top-right rotated */}
            <div
              className={`absolute top-0 right-0 font-display font-bold text-[14px] tracking-[0.18em] uppercase ${klas.color} border-[3px] border-double ${klas.border} px-3 py-1.5 -rotate-3 opacity-90`}
            >
              {klas.label}
              <span className="block text-[8px] tracking-wider mt-0.5 opacity-70 text-center">Klasifikasi BSSN</span>
            </div>
          </div>

          {!ready && (
            <div className="p-4 mb-4 border border-bluestamp/40 bg-bluestamp/5 font-mono text-xs text-bluestamp">
              ⏳ Dokumen sedang diproses (status: {doc.status}). Tunggu hingga READY untuk analisa.
            </div>
          )}

          {ready && view === 'dokumen'     && <DokumenSection docId={id!} doc={doc} />}
          {ready && view === 'lampiran'    && <LampiranSection docId={id!} />}
          {ready && view === 'ringkasan'   && <RingkasanSection docId={id!} />}
          {ready && view === 'temuan'      && <FindingsSection docId={id!} />}
          {ready && view === 'pasal'       && <ViolationSection docId={id!} />}
          {ready && view === 'kronologi'   && <TimelineSection docId={id!} />}
          {ready && view === 'kontradiksi' && <InconsistencySection docId={id!} />}
          {ready && view === 'entitas'     && <EntitySection docId={id!} />}
          {ready && view === 'pii'         && <PIISection docId={id!} />}
          {ready && view === 'asisten'     && (
            <div className="overflow-hidden" style={{ height: 'calc(100vh - 360px)' }}>
              <ChatPanel
                docId={id!}
                title={`AI Asisten — ${doc.bap_number || 'BAP'}`}
                subtitle="Konteks: BAP ini · RAG retrieval per dokumen · citation hal."
                sidebarWidth={240}
                minHeight="100%"
              />
            </div>
          )}
          {ready && view === 'ekspor'      && <ExportSection docId={id!} bapNumber={doc.bap_number} />}
        </main>
      </div>
    </div>
  );
}

// ============ RINGKASAN SECTION ============

type SummaryLevel = 'executive' | 'detailed' | 'bullet';

const LEVEL_LABEL: Record<SummaryLevel, { name: string; desc: string }> = {
  executive: { name: 'Executive', desc: 'Ringkas · 3-5 paragraf untuk briefing' },
  detailed:  { name: 'Detailed',  desc: 'Lengkap · semua bagian + analisa' },
  bullet:    { name: 'Bullet',    desc: 'Poin-poin kunci' },
};

function RingkasanSection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const [activeLevel, setActiveLevel] = useState<SummaryLevel>('executive');

  const { data: allSummaries } = useQuery<Record<SummaryLevel, SummaryResp | null>>({
    queryKey: ['summaries-all', docId],
    queryFn: () => api(`/api/v1/summary/${docId}/all`),
  });

  // Active summary job for THIS doc + level. Dock + button reflect same state.
  const activeJob = find(
    (j) =>
      j.kind === 'summary' &&
      j.doc_id === docId &&
      (j.params as Record<string, unknown>)?.level === activeLevel &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isGenerating = !!activeJob;

  // Refetch summaries when a summary job for this doc finishes.
  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'summary' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['summaries-all', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleGenerate = async (level: SummaryLevel) => {
    // Backend dedupes (user+doc+kind+params) within 5 min, so repeated clicks
    // collapse to the same job_id — UI stays consistent.
    await submit('summary', docId, { level });
  };

  const current = allSummaries?.[activeLevel] || null;

  return (
    <section>
      <div className="mb-5 flex items-center gap-3">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-brass" />
          Ringkasan
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— SUMMARIA</span>
        </h2>
      </div>

      {/* LEVEL TAB STRIP */}
      <div className="flex gap-1 mb-5 border-b-2 border-ink">
        {(Object.keys(LEVEL_LABEL) as SummaryLevel[]).map((lvl) => {
          const has = !!allSummaries?.[lvl];
          const active = activeLevel === lvl;
          return (
            <button
              key={lvl}
              onClick={() => setActiveLevel(lvl)}
              className={`relative px-4 py-2.5 -mb-px font-display text-[13px] tracking-tight border border-ink-line border-b-0 rounded-t-md flex items-center gap-2 transition-all ${
                active
                  ? 'bg-paper text-ink font-bold border-ink'
                  : 'bg-paper-2 text-ink-2 hover:bg-paper hover:text-ink'
              }`}
            >
              {LEVEL_LABEL[lvl].name}
              {has && <span className="w-1.5 h-1.5 rounded-full bg-olive" title="Sudah ter-generate" />}
            </button>
          );
        })}
        <div className="ml-auto self-center pb-2">
          <button
            onClick={() => handleGenerate(activeLevel)}
            disabled={isGenerating}
            className="px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
          >
            {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {current ? `Buat Ulang (${LEVEL_LABEL[activeLevel].name})` : `Generate ${LEVEL_LABEL[activeLevel].name}`}
          </button>
        </div>
      </div>

      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-4">
        {LEVEL_LABEL[activeLevel].desc}
      </p>

      {current && (
        <div className="bg-paper-2/40 p-8 border border-ink-line relative">
          <div className="absolute top-4 right-8 font-display font-bold text-[11px] tracking-widest uppercase text-maroon border-2 border-maroon px-2.5 py-1 rotate-3 opacity-40 pointer-events-none">
            Asisten AI
          </div>
          <div className="font-mono text-[10px] tracking-wider uppercase text-ink-3 border-t border-b border-ink-line py-1.5 mb-4 flex gap-4 flex-wrap">
            Disusun oleh <strong className="text-brass font-bold tracking-widest">{current.model}</strong>
            · {current.chunks_used} chunks
            · {current.tokens_in} token in · {current.tokens_out} token out
            <span className="ml-auto uppercase tracking-widest">{current.level}</span>
          </div>
          <MarkdownView>{current.summary}</MarkdownView>
          {current.citations && current.citations.length > 0 && (
            <div className="mt-6 pt-4 border-t border-ink-line">
              <div className="label-mono mb-3 text-brass">Sumber Chunks</div>
              <div className="grid grid-cols-2 gap-2 font-mono text-[10px] text-ink-2">
                {current.citations.slice(0, 10).map((c, i) => (
                  <div key={c.chunk_id} className="flex gap-2">
                    <span className="text-ink-3">#{i + 1}</span>
                    <span>p.{c.page_start}-{c.page_end}</span>
                    <span className="text-brass ml-auto">{(c.score || 0).toFixed(3)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!current && (
        <div className="text-center py-12 font-mono text-xs text-ink-3 border border-dashed border-ink-line">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
          Belum ada ringkasan <strong className="text-ink">{LEVEL_LABEL[activeLevel].name}</strong>.
          Klik "Generate" untuk membuat.
        </div>
      )}
    </section>
  );
}

// ============ FINDINGS SECTION ============

interface Finding {
  id: string;
  category: string;
  title: string;
  description: string;
  evidence_strength: string;
  confidence: number;
  severity_score: number;
  actors: string[];
  page_refs: number[];
  model_used: string;
}

const CATEGORY_STYLE: Record<string, { label: string; color: string; border: string }> = {
  PELANGGARAN:      { label: 'Pelanggaran',     color: 'text-sev-critical', border: 'border-sev-critical' },
  ANOMALI:          { label: 'Anomali',         color: 'text-sev-high',     border: 'border-sev-high' },
  FAKTA_KUNCI:      { label: 'Fakta Kunci',     color: 'text-sev-medium',   border: 'border-sev-medium' },
  PERTANYAAN_LANJUT:{ label: 'Pertanyaan',      color: 'text-bluestamp',    border: 'border-bluestamp' },
  KONTEKS:          { label: 'Konteks',         color: 'text-sev-low',      border: 'border-sev-low' },
};

function FindingsSection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const { data: findings, isLoading } = useQuery<Finding[]>({
    queryKey: ['findings', docId],
    queryFn: () => api(`/api/v1/findings/${docId}`),
  });

  const activeJob = find(
    (j) =>
      j.kind === 'findings' &&
      j.doc_id === docId &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isRunning = !!activeJob;
  const lastFailed = find(
    (j) => j.kind === 'findings' && j.doc_id === docId && j.status === 'FAILED',
  );

  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'findings' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['findings', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleExtract = async () => {
    await submit('findings', docId, { force: true });
  };

  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-brass" />
          Daftar Temuan
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— INVENTIO</span>
        </h2>
        <button
          onClick={handleExtract}
          disabled={isRunning}
          className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {findings && findings.length > 0 ? 'Extract Ulang' : 'Extract Temuan'}
        </button>
      </div>

      {isRunning && (
        <div className="p-4 border border-brass/40 bg-brass-soft/30 font-mono text-xs text-brass mb-4">
          ⏳ {(activeJob?.stage || 'queued').replace(/_/g, ' ').toUpperCase()} — pantau di dock kanan bawah, halaman aman untuk dipindah.
        </div>
      )}

      {!isRunning && lastFailed && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Job sebelumnya gagal: {lastFailed.error || 'unknown'}
        </div>
      )}

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {findings && findings.length === 0 && !isRunning && (
        <div className="text-center py-8 font-mono text-xs text-ink-3">
          <ListChecks className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Belum ada temuan. Klik "Extract Temuan" untuk analisa AI.
        </div>
      )}

      {findings && findings.length > 0 && (
        <div className="flex flex-col">
          {findings.map((f) => {
            const cs = CATEGORY_STYLE[f.category] || CATEGORY_STYLE.KONTEKS;
            return (
              <div
                key={f.id}
                className="grid grid-cols-[60px_1fr_auto] gap-4 py-4 border-b border-ink-line hover:bg-brass-soft/20 cursor-pointer"
              >
                <div>
                  <div className="font-display font-bold text-[28px] leading-none tracking-tighter text-maroon tabular-nums">
                    {f.severity_score.toFixed(1)}
                  </div>
                  <div className="label-mono mt-1 text-[9px]">Skor</div>
                </div>
                <div>
                  <div className="font-display font-semibold text-[15px] leading-snug mb-1">
                    {f.title}
                  </div>
                  <p className="text-[13px] text-ink-2 mb-2 leading-snug">{f.description}</p>
                  <div className="flex gap-2 flex-wrap font-mono text-[10px] uppercase tracking-wider">
                    <span className={`px-1.5 py-0.5 border ${cs.color} ${cs.border} font-bold`}>
                      {cs.label} · {f.evidence_strength}
                    </span>
                    {f.page_refs.length > 0 && (
                      <span className="px-1.5 py-0.5 text-ink-3">
                        Hal. {f.page_refs.join(',')}
                      </span>
                    )}
                    {f.actors.length > 0 && (
                      <span className="px-1.5 py-0.5 text-bluestamp">
                        Aktor: {f.actors.join(', ')}
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 text-ink-3">
                      Conf: {(f.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div className="text-ink-3 font-mono text-sm">→</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ VIOLATION (PASAL INDIKATIF) SECTION ============

interface Violation {
  id: string;
  regulation_id: string;
  regulation_name: string;
  regulation_full_name: string;
  regulation_category: string;
  penalty: string | null;
  act_summary: string;
  act_context: string;
  page_refs: number[];
  elements_met: Array<{ element: string; evidence?: string }>;
  elements_missing: Array<{ element: string; reason?: string }>;
  confidence: number;
  explanation: string;
  is_verified: boolean;
  model_used: string;
  created_at: string;
}

function ViolationSection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const { data: violations, isLoading } = useQuery<Violation[]>({
    queryKey: ['violations', docId],
    queryFn: () => api(`/api/v1/violation/${docId}`),
  });

  const activeJob = find(
    (j) =>
      j.kind === 'violation' &&
      j.doc_id === docId &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isRunning = !!activeJob;
  const lastFailed = find(
    (j) => j.kind === 'violation' && j.doc_id === docId && j.status === 'FAILED',
  );

  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'violation' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['violations', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleScan = async () => {
    await submit('violation', docId, { force: true });
  };

  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <Gavel className="w-5 h-5 text-brass" />
          Pasal Indikatif
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— ARTICULI</span>
        </h2>
        <button
          onClick={handleScan}
          disabled={isRunning}
          className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {violations && violations.length > 0 ? 'Scan Ulang' : 'Scan Pasal'}
        </button>
      </div>

      <div className="p-3 mb-4 border border-bluestamp/30 bg-bluestamp/5 font-mono text-[10px] uppercase tracking-wider text-bluestamp">
        ⚠ INDIKATIF · Penetapan pasal akhir wewenang Penyidik & JPU sesuai hukum acara
      </div>

      {isRunning && (
        <div className="p-4 border border-brass/40 bg-brass-soft/30 font-mono text-xs text-brass mb-4">
          ⏳ {(activeJob?.stage || 'queued').replace(/_/g, ' ').toUpperCase()} — pantau di dock kanan bawah, halaman aman untuk dipindah.
        </div>
      )}

      {!isRunning && lastFailed && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Job sebelumnya gagal: {lastFailed.error || 'unknown'}
        </div>
      )}

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {violations && violations.length === 0 && !isRunning && (
        <div className="text-center py-8 font-mono text-xs text-ink-3">
          <Gavel className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Belum ada pasal indikatif. Klik "Scan Pasal" untuk analisa.
        </div>
      )}

      {violations && violations.length > 0 && (
        <div className="flex flex-col gap-4">
          {violations.map((v) => {
            const conf = v.confidence;
            const confClass =
              conf >= 0.8 ? 'text-sev-critical border-sev-critical' :
              conf >= 0.6 ? 'text-sev-high border-sev-high' :
              'text-sev-medium border-sev-medium';
            return (
              <div key={v.id} className="bg-paper-2/40 border border-ink-line p-5">
                <div className="flex items-start justify-between gap-4 mb-3 pb-3 border-b border-ink-line">
                  <div>
                    <div className="font-display font-bold text-[18px] leading-tight mb-1">
                      {v.regulation_name}
                    </div>
                    <p className="font-mono text-[10px] tracking-wider uppercase text-ink-3">
                      {v.regulation_category} · {v.penalty || '—'}
                    </p>
                  </div>
                  <div className={`text-right`}>
                    <div className={`font-display font-bold text-[28px] leading-none tracking-tighter tabular-nums ${confClass.split(' ')[0]}`}>
                      {(conf * 100).toFixed(0)}%
                    </div>
                    <div className="label-mono text-[9px] mt-1">Confidence</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="label-mono text-olive mb-2 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Unsur Terpenuhi ({v.elements_met.length})
                    </div>
                    <ul className="text-[12px] space-y-1.5 text-ink">
                      {v.elements_met.map((e, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-olive">✓</span>
                          <div>
                            <span className="font-semibold">{e.element}</span>
                            {e.evidence && (
                              <p className="text-[11px] text-ink-2 italic mt-0.5">{e.evidence}</p>
                            )}
                          </div>
                        </li>
                      ))}
                      {v.elements_met.length === 0 && (
                        <li className="text-ink-3 italic">—</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="label-mono text-crimson mb-2 flex items-center gap-1">
                      <X className="w-3 h-3" />
                      Unsur Belum Terpenuhi ({v.elements_missing.length})
                    </div>
                    <ul className="text-[12px] space-y-1.5 text-ink">
                      {v.elements_missing.map((e, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-crimson">✗</span>
                          <div>
                            <span className="font-semibold">{e.element}</span>
                            {e.reason && (
                              <p className="text-[11px] text-ink-2 italic mt-0.5">{e.reason}</p>
                            )}
                          </div>
                        </li>
                      ))}
                      {v.elements_missing.length === 0 && (
                        <li className="text-ink-3 italic">— Semua unsur terpenuhi</li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="pt-3 border-t border-ink-line">
                  <div className="label-mono mb-2 text-brass">Analisa Asisten AI</div>
                  <p className="font-serif text-[14px] leading-snug text-ink mb-2">{v.explanation}</p>
                  <div className="flex gap-2 flex-wrap font-mono text-[10px] uppercase tracking-wider mt-2">
                    {v.page_refs.length > 0 && (
                      <span className="px-1.5 py-0.5 text-ink-3 border border-ink-line">
                        Pendukung: Hal. {v.page_refs.join(',')}
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 text-ink-3 border border-ink-line">
                      Perbuatan: {v.act_summary.slice(0, 80)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ INCONSISTENCY (KONTRADIKSI) SECTION ============

interface Inconsistency {
  id: string;
  inconsistency_type: string;
  topic: string;
  claim_a: string;
  claim_b: string;
  page_a: number | null;
  page_b: number | null;
  actors: string[];
  severity_score: number;
  confidence: number;
  explanation: string;
  is_verified: boolean;
  model_used: string;
  created_at: string;
}

const INC_TYPE_STYLE: Record<string, { label: string; color: string; border: string }> = {
  TEMPORAL:  { label: 'Waktu',      color: 'text-sev-high',     border: 'border-sev-high' },
  NUMERIC:   { label: 'Angka',      color: 'text-maroon',       border: 'border-maroon' },
  IDENTITY:  { label: 'Identitas',  color: 'text-sev-critical', border: 'border-sev-critical' },
  STATEMENT: { label: 'Pernyataan', color: 'text-bluestamp',    border: 'border-bluestamp' },
  LOCATION:  { label: 'Lokasi',     color: 'text-sev-medium',   border: 'border-sev-medium' },
};

function InconsistencySection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const { data: items, isLoading } = useQuery<Inconsistency[]>({
    queryKey: ['inconsistencies', docId],
    queryFn: () => api(`/api/v1/inconsistency/${docId}`),
  });

  const activeJob = find(
    (j) =>
      j.kind === 'inconsistency' &&
      j.doc_id === docId &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isRunning = !!activeJob;
  const lastFailed = find(
    (j) => j.kind === 'inconsistency' && j.doc_id === docId && j.status === 'FAILED',
  );

  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'inconsistency' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['inconsistencies', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleScan = async () => {
    await submit('inconsistency', docId, { force: true });
  };

  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <GitCompareArrows className="w-5 h-5 text-brass" />
          Kontradiksi Internal
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— DISCREPANTIA</span>
        </h2>
        <button
          onClick={handleScan}
          disabled={isRunning}
          className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {items && items.length > 0 ? 'Scan Ulang' : 'Cari Kontradiksi'}
        </button>
      </div>

      <div className="p-3 mb-4 border border-bluestamp/30 bg-bluestamp/5 font-mono text-[10px] uppercase tracking-wider text-bluestamp">
        ⚠ DUGAAN · Bandingkan klaim antar halaman BAP. Verifikasi manual sebelum ditindak.
      </div>

      {isRunning && (
        <div className="p-4 border border-brass/40 bg-brass-soft/30 font-mono text-xs text-brass mb-4">
          ⏳ {(activeJob?.stage || 'queued').replace(/_/g, ' ').toUpperCase()} — pantau di dock kanan bawah, halaman aman untuk dipindah.
        </div>
      )}

      {!isRunning && lastFailed && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Job sebelumnya gagal: {lastFailed.error || 'unknown'}
        </div>
      )}

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {items && items.length === 0 && !isRunning && (
        <div className="text-center py-8 font-mono text-xs text-ink-3">
          <GitCompareArrows className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Belum ada kontradiksi terdeteksi. Klik "Cari Kontradiksi" untuk analisa.
        </div>
      )}

      {items && items.length > 0 && (
        <div className="flex flex-col gap-4">
          {items.map((it) => {
            const ts = INC_TYPE_STYLE[it.inconsistency_type] || INC_TYPE_STYLE.STATEMENT;
            return (
              <div key={it.id} className="bg-paper-2/40 border border-ink-line p-5">
                <div className="grid grid-cols-[60px_1fr_auto] gap-4 mb-3 pb-3 border-b border-ink-line items-start">
                  <div>
                    <div className="font-display font-bold text-[28px] leading-none tracking-tighter text-maroon tabular-nums">
                      {it.severity_score.toFixed(1)}
                    </div>
                    <div className="label-mono mt-1 text-[9px]">Skor</div>
                  </div>
                  <div>
                    <div className="font-display font-semibold text-[15px] leading-snug mb-1">
                      {it.topic}
                    </div>
                    <div className="flex gap-2 flex-wrap font-mono text-[10px] uppercase tracking-wider">
                      <span className={`px-1.5 py-0.5 border ${ts.color} ${ts.border} font-bold`}>
                        {ts.label}
                      </span>
                      {it.actors.length > 0 && (
                        <span className="px-1.5 py-0.5 text-bluestamp">
                          Aktor: {it.actors.join(', ')}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 text-ink-3">
                        Conf: {(it.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-ink-3 font-mono text-sm">⇄</div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="border-l-2 border-bluestamp pl-3">
                    <div className="label-mono text-bluestamp mb-1 flex items-center gap-1">
                      Klaim A {it.page_a != null && <span className="text-ink-3">· Hal. {it.page_a}</span>}
                    </div>
                    <p className="font-serif text-[13px] leading-snug text-ink italic">
                      "{it.claim_a}"
                    </p>
                  </div>
                  <div className="border-l-2 border-crimson pl-3">
                    <div className="label-mono text-crimson mb-1 flex items-center gap-1">
                      Klaim B {it.page_b != null && <span className="text-ink-3">· Hal. {it.page_b}</span>}
                    </div>
                    <p className="font-serif text-[13px] leading-snug text-ink italic">
                      "{it.claim_b}"
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-ink-line">
                  <div className="label-mono mb-2 text-brass">Analisa Asisten AI</div>
                  <p className="font-serif text-[14px] leading-snug text-ink">{it.explanation}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ TIMELINE (KRONOLOGI) SECTION ============

interface TimelineEvt {
  id: string;
  event_type: string;
  event_date: string | null;
  event_time: string | null;
  date_precision: string;
  raw_date_text: string | null;
  title: string;
  description: string;
  actors: string[];
  location: string | null;
  page_refs: number[];
  confidence: number;
  is_verified: boolean;
  model_used: string;
  created_at: string;
}

const EVENT_TYPE_STYLE: Record<string, { label: string; color: string; border: string; dot: string }> = {
  KEJADIAN:      { label: 'Kejadian',      color: 'text-sev-critical', border: 'border-sev-critical', dot: 'bg-sev-critical' },
  PEMERIKSAAN:   { label: 'Pemeriksaan',   color: 'text-bluestamp',    border: 'border-bluestamp',    dot: 'bg-bluestamp' },
  PELAPORAN:     { label: 'Pelaporan',     color: 'text-sev-high',     border: 'border-sev-high',     dot: 'bg-sev-high' },
  BUKTI:         { label: 'Bukti',         color: 'text-maroon',       border: 'border-maroon',       dot: 'bg-maroon' },
  ADMINISTRATIF: { label: 'Administratif', color: 'text-ink-2',        border: 'border-ink-2',        dot: 'bg-ink-2' },
};

function fmtDate(iso: string | null, precision: string, raw: string | null): string {
  if (!iso) return raw || '— Tanggal tidak pasti';
  const d = new Date(iso);
  if (precision === 'YEAR') return d.getFullYear().toString();
  if (precision === 'MONTH') return d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

function TimelineSection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const { data: items, isLoading } = useQuery<TimelineEvt[]>({
    queryKey: ['timeline', docId],
    queryFn: () => api(`/api/v1/timeline/${docId}`),
  });

  const activeJob = find(
    (j) =>
      j.kind === 'timeline' &&
      j.doc_id === docId &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isRunning = !!activeJob;
  const lastFailed = find(
    (j) => j.kind === 'timeline' && j.doc_id === docId && j.status === 'FAILED',
  );

  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'timeline' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['timeline', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleExtract = async () => {
    await submit('timeline', docId, { force: true });
  };

  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-brass" />
          Kronologi
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— CHRONOLOGIA</span>
        </h2>
        <button
          onClick={handleExtract}
          disabled={isRunning}
          className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {items && items.length > 0 ? 'Bangun Ulang' : 'Bangun Kronologi'}
        </button>
      </div>

      {isRunning && (
        <div className="p-4 border border-brass/40 bg-brass-soft/30 font-mono text-xs text-brass mb-4">
          ⏳ {(activeJob?.stage || 'queued').replace(/_/g, ' ').toUpperCase()} — pantau di dock kanan bawah, halaman aman untuk dipindah.
        </div>
      )}

      {!isRunning && lastFailed && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Job sebelumnya gagal: {lastFailed.error || 'unknown'}
        </div>
      )}

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {items && items.length === 0 && !isRunning && (
        <div className="text-center py-8 font-mono text-xs text-ink-3">
          <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Belum ada event kronologi. Klik "Bangun Kronologi".
        </div>
      )}

      {items && items.length > 0 && (
        <div className="relative pl-8 border-l-2 border-ink-line">
          {items.map((e) => {
            const ts = EVENT_TYPE_STYLE[e.event_type] || EVENT_TYPE_STYLE.KEJADIAN;
            return (
              <div key={e.id} className="relative mb-6 last:mb-0">
                <div className={`absolute -left-[37px] top-1.5 w-3 h-3 ${ts.dot} border-2 border-paper rounded-full`}></div>
                <div className="bg-paper-2/30 border border-ink-line p-4">
                  <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                    <div className="font-display font-bold text-[15px] tracking-tight">
                      {fmtDate(e.event_date, e.date_precision, e.raw_date_text)}
                    </div>
                    {e.event_time && (
                      <span className="font-mono text-[11px] text-bluestamp">{e.event_time}</span>
                    )}
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${ts.color} ${ts.border} font-bold`}>
                      {ts.label}
                    </span>
                    {e.date_precision === 'APPROX' && (
                      <span className="font-mono text-[10px] uppercase text-ink-3">≈ Perkiraan</span>
                    )}
                    <span className="ml-auto font-mono text-[10px] text-ink-3">
                      Conf: {(e.confidence * 100).toFixed(0)}%
                    </span>
                  </div>

                  <div className="font-display font-semibold text-[14px] leading-snug mb-1">
                    {e.title}
                  </div>
                  <p className="text-[13px] text-ink-2 leading-snug mb-2">{e.description}</p>

                  <div className="flex gap-2 flex-wrap font-mono text-[10px] uppercase tracking-wider">
                    {e.actors.length > 0 && (
                      <span className="px-1.5 py-0.5 text-bluestamp border border-bluestamp/30">
                        Aktor: {e.actors.join(', ')}
                      </span>
                    )}
                    {e.location && (
                      <span className="px-1.5 py-0.5 text-ink-2 border border-ink-line flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5" />
                        {e.location}
                      </span>
                    )}
                    {e.page_refs.length > 0 && (
                      <span className="px-1.5 py-0.5 text-ink-3 border border-ink-line">
                        Hal. {e.page_refs.join(',')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ PII (DATA PRIBADI) SECTION ============

interface PIIDet {
  id: string;
  label: string;
  original_text: string;
  replacement: string;
  pseudonym: string | null;
  page: number | null;
  source: string;
  confidence: number;
  is_false_positive: boolean;
  created_at: string;
}

const PII_LABEL_STYLE: Record<string, { name: string; color: string; border: string }> = {
  NIK:          { name: 'NIK',         color: 'text-sev-critical', border: 'border-sev-critical' },
  NPWP:         { name: 'NPWP',        color: 'text-maroon',       border: 'border-maroon' },
  EMAIL:        { name: 'Email',       color: 'text-bluestamp',    border: 'border-bluestamp' },
  PHONE:        { name: 'Telepon',     color: 'text-bluestamp',    border: 'border-bluestamp' },
  BANK_ACCOUNT: { name: 'Rekening',    color: 'text-sev-critical', border: 'border-sev-critical' },
  PLATE:        { name: 'Plat',        color: 'text-sev-medium',   border: 'border-sev-medium' },
  PERSON:       { name: 'Nama Orang',  color: 'text-sev-high',     border: 'border-sev-high' },
  ADDRESS:      { name: 'Alamat',      color: 'text-sev-medium',   border: 'border-sev-medium' },
};

function PIISection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const [mode, setMode] = useState<'REDACT' | 'PSEUDONYM'>('REDACT');
  const [maskedText, setMaskedText] = useState<string | null>(null);

  const { data: items, isLoading, isError, error } = useQuery<PIIDet[]>({
    queryKey: ['pii', docId],
    queryFn: () => api(`/api/v1/pii/${docId}`),
  });

  const activeJob = find(
    (j) =>
      j.kind === 'pii_llm' &&
      j.doc_id === docId &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isRunningScan = !!activeJob;
  const lastFailed = find(
    (j) => j.kind === 'pii_llm' && j.doc_id === docId && j.status === 'FAILED',
  );

  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'pii_llm' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['pii', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleScan = async () => {
    await submit('pii_llm', docId, { force: true });
  };

  const fpMut = useMutation({
    mutationFn: async ({ id, fp }: { id: string; fp: boolean }) => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/pii/detection/${id}/false-positive`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
          body: JSON.stringify({ is_false_positive: fp }),
        },
      );
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pii', docId] }),
  });

  const previewMut = useMutation({
    mutationFn: async () => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/pii/${docId}/masked-text?mode=${mode}`,
        { headers: { 'X-Dev-User': 'admin@netra.local' } },
      );
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json() as Promise<{ mode: string; text: string; length: number }>;
    },
    onSuccess: (data) => setMaskedText(data.text),
  });

  const downloadMasked = () => {
    if (!maskedText) return;
    const blob = new Blob([maskedText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BAP-${docId.slice(0, 8)}-MASKED-${mode}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group by label
  const grouped: Record<string, PIIDet[]> = {};
  (items || []).forEach((d) => {
    grouped[d.label] = grouped[d.label] || [];
    grouped[d.label].push(d);
  });

  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <EyeOff className="w-5 h-5 text-brass" />
          Data Pribadi
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— DATA SENSITIVA</span>
        </h2>
        <button
          onClick={handleScan}
          disabled={isRunningScan}
          className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
        >
          {isRunningScan ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {items && items.length > 0 ? 'Scan Ulang' : 'Scan PII'}
        </button>
      </div>

      {items && items.length > 0 && (
        <div className="p-3 mb-4 border border-maroon/30 bg-maroon/5 font-mono text-[10px] uppercase tracking-wider text-maroon">
          ⚠ SENSITIF · PII harus disamarkan sebelum BAP diekspor / dipakai untuk pelatihan model
        </div>
      )}

      {isRunningScan && (
        <div className="p-4 border border-brass/40 bg-brass-soft/30 font-mono text-xs text-brass mb-4">
          ⏳ {(activeJob?.stage || 'queued').replace(/_/g, ' ').toUpperCase()} — pantau di dock kanan bawah, halaman aman untuk dipindah.
        </div>
      )}

      {!isRunningScan && lastFailed && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Job sebelumnya gagal: {lastFailed.error || 'unknown'}
        </div>
      )}

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {isError && !isLoading && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Gagal memuat deteksi PII: {String(error)}
        </div>
      )}

      {items && items.length === 0 && !isRunningScan && (
        <div className="text-center py-8 font-mono text-xs text-ink-3">
          <EyeOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Belum ada deteksi PII untuk BAP ini. Klik "Scan PII" untuk menjalankan deteksi (regex + LLM).
        </div>
      )}

      {items && items.length > 0 && (
        <>
          {/* SUMMARY CHIPS */}
          <div className="flex gap-2 flex-wrap mb-5">
            {Object.entries(grouped).map(([label, list]) => {
              const ls = PII_LABEL_STYLE[label] || { name: label, color: 'text-ink-2', border: 'border-ink-2' };
              const active = list.filter((d) => !d.is_false_positive).length;
              return (
                <div
                  key={label}
                  className={`px-3 py-1.5 border ${ls.border} ${ls.color} font-mono text-[11px] uppercase tracking-wider font-bold flex items-baseline gap-2`}
                >
                  {ls.name}
                  <span className="font-display text-[18px] tabular-nums">{active}</span>
                  {active !== list.length && (
                    <span className="text-ink-3 text-[9px]">/ {list.length}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* DETECTION TABLE */}
          <div className="border border-ink-line mb-6">
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="border-b border-ink bg-paper-2/40 text-ink-3 uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Label</th>
                  <th className="text-left px-3 py-2">Nilai Asli</th>
                  <th className="text-left px-3 py-2">Pseudonym</th>
                  <th className="text-left px-3 py-2 w-20">Hal.</th>
                  <th className="text-left px-3 py-2 w-20">Source</th>
                  <th className="text-left px-3 py-2 w-20">Conf</th>
                  <th className="text-left px-3 py-2 w-24">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => {
                  const ls = PII_LABEL_STYLE[d.label] || { name: d.label, color: 'text-ink-2', border: 'border-ink-2' };
                  return (
                    <tr
                      key={d.id}
                      className={`border-b border-ink-line/60 ${d.is_false_positive ? 'opacity-40 line-through' : ''}`}
                    >
                      <td className={`px-3 py-1.5 ${ls.color} font-bold`}>{ls.name}</td>
                      <td className="px-3 py-1.5 text-ink">{d.original_text}</td>
                      <td className="px-3 py-1.5 text-bluestamp">{d.pseudonym || '—'}</td>
                      <td className="px-3 py-1.5 text-ink-3">{d.page ?? '—'}</td>
                      <td className="px-3 py-1.5 text-ink-3">{d.source}</td>
                      <td className="px-3 py-1.5 text-ink-3">{(d.confidence * 100).toFixed(0)}%</td>
                      <td className="px-3 py-1.5">
                        <button
                          onClick={() => fpMut.mutate({ id: d.id, fp: !d.is_false_positive })}
                          className="text-[10px] underline text-ink-3 hover:text-crimson"
                        >
                          {d.is_false_positive ? 'Aktifkan' : 'Tandai FP'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* MASKED PREVIEW CONTROLS */}
          <div className="flex items-center gap-3 mb-3">
            <div className="label-mono">Mode masking:</div>
            <div className="flex gap-1">
              {(['REDACT', 'PSEUDONYM'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider font-semibold border transition ${
                    mode === m ? 'bg-ink text-paper border-ink' : 'border-ink-line text-ink-2 hover:border-ink'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
            <button
              onClick={() => previewMut.mutate()}
              disabled={previewMut.isPending}
              className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
            >
              {previewMut.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
              Render Preview
            </button>
            {maskedText && (
              <button
                onClick={downloadMasked}
                className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold border border-bluestamp text-bluestamp hover:bg-bluestamp hover:text-paper transition flex items-center gap-2"
              >
                <Download className="w-3 h-3" />
                Unduh .txt
              </button>
            )}
          </div>

          {maskedText && (
            <div className="bg-paper-2/40 p-5 border border-ink-line max-h-[400px] overflow-auto">
              <pre className="font-mono text-[11px] text-ink whitespace-pre-wrap leading-relaxed">
                {maskedText}
              </pre>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ============ ENTITY (NER + GRAPH) SECTION ============

interface EntityRec {
  id: string;
  entity_type: string;
  canonical_name: string;
  aliases: string[];
  role: string | null;
  description: string | null;
  page_refs: number[];
  mention_count: number;
  confidence: number;
  is_verified: boolean;
}

interface RelationRec {
  id: string;
  relation_type: string;
  description: string;
  page_refs: number[];
  confidence: number;
  source_id: string;
  source_name: string;
  source_type: string;
  target_id: string;
  target_name: string;
  target_type: string;
  created_at: string;
}

const ENTITY_TYPE_STYLE: Record<string, { label: string; color: string; border: string; Icon: any }> = {
  PERSON:       { label: 'Orang',       color: 'text-sev-high',     border: 'border-sev-high',     Icon: UserIcon },
  ORGANIZATION: { label: 'Organisasi',  color: 'text-bluestamp',    border: 'border-bluestamp',    Icon: Building2 },
  LOCATION:     { label: 'Lokasi',      color: 'text-sev-medium',   border: 'border-sev-medium',   Icon: MapPin },
  OBJECT:       { label: 'Objek',       color: 'text-ink-2',        border: 'border-ink-2',        Icon: Box },
  MONEY:        { label: 'Uang',        color: 'text-olive',        border: 'border-olive',        Icon: Banknote },
  EVENT:        { label: 'Event',       color: 'text-maroon',       border: 'border-maroon',       Icon: CalendarClock },
};

const REL_TYPE_LABEL: Record<string, string> = {
  TRANSFERRED_TO: 'mentransfer ke',
  RECEIVED_FROM:  'menerima dari',
  EMPLOYED_BY:    'bekerja di',
  OWNS:           'memiliki',
  REPORTED_BY:    'dilaporkan oleh',
  WITNESSED_BY:   'disaksikan oleh',
  INVESTIGATED_BY:'diperiksa oleh',
  ALLEGED_AGAINST:'dilaporkan terhadap',
  LOCATED_AT:     'berlokasi di',
  RELATED_TO:     'terkait dengan',
};

function EntitySection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const { submit, find, onTerminal } = useJobs();
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const { data: entities, isLoading: entLoading } = useQuery<EntityRec[]>({
    queryKey: ['entities', docId],
    queryFn: () => api(`/api/v1/entity/${docId}/entities`),
  });

  const { data: relations, isLoading: relLoading } = useQuery<RelationRec[]>({
    queryKey: ['relations', docId],
    queryFn: () => api(`/api/v1/entity/${docId}/relations`),
  });

  const { data: graphData } = useQuery<{ nodes: any[]; edges: any[] }>({
    queryKey: ['entity-graph-data', docId],
    queryFn: () => api(`/api/v1/entity/${docId}/graph-data?max_nodes=40`),
    enabled: !!entities && entities.length > 0,
  });

  const activeJob = find(
    (j) =>
      j.kind === 'entity' &&
      j.doc_id === docId &&
      (j.status === 'PENDING' || j.status === 'RUNNING'),
  );
  const isRunning = !!activeJob;
  const lastFailed = find(
    (j) => j.kind === 'entity' && j.doc_id === docId && j.status === 'FAILED',
  );

  useEffect(() => {
    const off = onTerminal((j) => {
      if (j.kind === 'entity' && j.doc_id === docId && j.status === 'DONE') {
        qc.invalidateQueries({ queryKey: ['entities', docId] });
        qc.invalidateQueries({ queryKey: ['relations', docId] });
        qc.invalidateQueries({ queryKey: ['entity-graph-data', docId] });
      }
    });
    return off;
  }, [docId, onTerminal, qc]);

  const handleExtract = async () => {
    await submit('entity', docId, { force: true });
  };

  // Group entities by type
  const grouped: Record<string, EntityRec[]> = {};
  (entities || []).forEach((e) => {
    grouped[e.entity_type] = grouped[e.entity_type] || [];
    grouped[e.entity_type].push(e);
  });

  const filteredEntities = typeFilter
    ? (entities || []).filter((e) => e.entity_type === typeFilter)
    : entities || [];

  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <Network className="w-5 h-5 text-brass" />
          Jaringan Entitas
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— RELATIONES</span>
        </h2>
        <button
          onClick={handleExtract}
          disabled={isRunning}
          className="ml-auto px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-semibold bg-brass-soft border border-brass text-brass hover:bg-brass hover:text-paper transition disabled:opacity-50 flex items-center gap-2"
        >
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {entities && entities.length > 0 ? 'Ekstrak Ulang' : 'Ekstrak Entitas'}
        </button>
      </div>

      {isRunning && (
        <div className="p-4 border border-brass/40 bg-brass-soft/30 font-mono text-xs text-brass mb-4">
          ⏳ {(activeJob?.stage || 'queued').replace(/_/g, ' ').toUpperCase()} — pantau di dock kanan bawah, halaman aman untuk dipindah.
        </div>
      )}

      {!isRunning && lastFailed && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Job sebelumnya gagal: {lastFailed.error || 'unknown'}
        </div>
      )}

      {(entLoading || relLoading) && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      {entities && entities.length === 0 && !isRunning && (
        <div className="text-center py-8 font-mono text-xs text-ink-3">
          <Network className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Belum ada entitas. Klik "Ekstrak Entitas".
        </div>
      )}

      {entities && entities.length > 0 && (
        <>
          {/* INTERACTIVE RELATION DIAGRAM — FULL WIDTH */}
          {graphData && graphData.nodes.length > 0 && (
            <div className="mb-6 border border-ink-line p-3 bg-paper-2/10 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-2 font-mono text-[10px] uppercase tracking-widest text-ink-3">
                <Network className="w-3 h-3 text-brass" />
                Diagram Relasi · {graphData.nodes.length} entitas · {graphData.edges.length} relasi
                <span className="ml-auto text-ink-3 italic normal-case tracking-normal">
                  Drag node untuk geser · klik node untuk highlight relasi
                </span>
              </div>
              <EntityGraph data={graphData} height={620} />
            </div>
          )}

          {/* TYPE FILTER CHIPS */}
          <div className="flex gap-2 flex-wrap mb-5">
            <button
              onClick={() => setTypeFilter(null)}
              className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold border transition ${
                !typeFilter ? 'bg-ink text-paper border-ink' : 'border-ink-line text-ink-2 hover:border-ink'
              }`}
            >
              Semua ({entities.length})
            </button>
            {Object.entries(grouped).map(([t, list]) => {
              const ts = ENTITY_TYPE_STYLE[t] || ENTITY_TYPE_STYLE.PERSON;
              const Icon = ts.Icon;
              const active = typeFilter === t;
              return (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold border flex items-center gap-1.5 transition ${
                    active ? `bg-ink text-paper border-ink` : `${ts.border} ${ts.color} hover:bg-paper-2`
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {ts.label} ({list.length})
                </button>
              );
            })}
          </div>

          {/* ENTITY GRID */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {filteredEntities.map((e) => {
              const ts = ENTITY_TYPE_STYLE[e.entity_type] || ENTITY_TYPE_STYLE.PERSON;
              return (
                <div key={e.id} className="bg-paper-2/30 border border-ink-line p-3">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="font-display font-bold text-[14px] tracking-tight">
                      {e.canonical_name}
                    </div>
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${ts.color} ${ts.border}`}>
                      {ts.label}
                    </span>
                  </div>
                  {e.role && (
                    <div className="font-mono text-[10px] uppercase tracking-wider text-bluestamp mb-1">
                      {e.role}
                    </div>
                  )}
                  {e.description && (
                    <p className="text-[12px] text-ink-2 leading-snug mb-2">{e.description}</p>
                  )}
                  <div className="flex gap-2 flex-wrap font-mono text-[10px] text-ink-3">
                    <span>×{e.mention_count} mention</span>
                    {e.page_refs.length > 0 && (
                      <span>Hal. {e.page_refs.join(',')}</span>
                    )}
                    <span className="ml-auto">{(e.confidence * 100).toFixed(0)}%</span>
                  </div>
                  {e.aliases.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-ink-line font-mono text-[10px] text-ink-3">
                      alias: {e.aliases.slice(0, 4).join(' · ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* RELATIONS */}
          {relations && relations.length > 0 && (
            <div>
              <div className="label-mono text-brass mb-3">Relasi Antar Entitas ({relations.length})</div>
              <div className="border border-ink-line">
                {relations.map((r) => {
                  const srcTs = ENTITY_TYPE_STYLE[r.source_type] || ENTITY_TYPE_STYLE.PERSON;
                  const tgtTs = ENTITY_TYPE_STYLE[r.target_type] || ENTITY_TYPE_STYLE.PERSON;
                  return (
                    <div key={r.id} className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center px-4 py-2.5 border-b border-ink-line/60 last:border-b-0 hover:bg-brass-soft/20">
                      <div className={`text-right font-display font-semibold text-[13px] ${srcTs.color}`}>
                        {r.source_name}
                      </div>
                      <div className="flex flex-col items-center gap-0.5 min-w-[120px]">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-ink-3">
                          {REL_TYPE_LABEL[r.relation_type] || r.relation_type.toLowerCase()}
                        </span>
                        <div className="w-full border-t border-dashed border-brass relative">
                          <span className="absolute right-0 -top-[7px] text-brass text-[12px]">▶</span>
                        </div>
                        {r.page_refs.length > 0 && (
                          <span className="font-mono text-[9px] text-ink-3">hal. {r.page_refs.join(',')}</span>
                        )}
                      </div>
                      <div className={`text-left font-display font-semibold text-[13px] ${tgtTs.color}`}>
                        {r.target_name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ============ EXPORT REPORT SECTION ============

function ExportSection({ docId, bapNumber }: { docId: string; bapNumber: string | null }) {
  const [maskPII, setMaskPII] = useState(true);
  const [isLoading, setIsLoading] = useState<'docx' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const exportFile = async (format: 'docx' | 'pdf') => {
    setIsLoading(format);
    setError(null);
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/export/${docId}/${format}?mask_pii=${maskPII}`,
        { headers: { 'X-Dev-User': 'admin@netra.local' } },
      );
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const filename = resp.headers.get('x-filename') || `LAPORAN-${docId.slice(0, 8)}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <section className="mt-12 mb-8">
      <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-ink">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <FileText className="w-5 h-5 text-brass" />
          Ekspor Laporan
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— EXPORTATIO</span>
        </h2>
      </div>

      <div className="bg-paper-2/40 border border-ink-line p-5">
        <p className="font-serif text-[14px] leading-snug text-ink mb-4">
          Bangun dokumen laporan analisa BAP <strong>{bapNumber || docId.slice(0, 8)}</strong> dalam format
          Microsoft Word (.docx). Laporan menggabungkan: Ringkasan, Daftar Temuan, Pasal Indikatif,
          Kronologi, Kontradiksi, dan Jaringan Entitas — termasuk watermark "INDIKATIF" pada setiap halaman.
        </p>

        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider cursor-pointer">
            <input
              type="checkbox"
              checked={maskPII}
              onChange={(e) => setMaskPII(e.target.checked)}
              className="w-4 h-4 accent-brass"
            />
            <span className={maskPII ? 'text-brass font-bold' : 'text-ink-3'}>
              Samarkan PII (nama, NIK, alamat, dll)
            </span>
          </label>
          {!maskPII && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-crimson border border-crimson px-2 py-0.5">
              ⚠ Mode RAW · data pribadi tidak disamarkan
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => exportFile('docx')}
            disabled={!!isLoading}
            className="px-5 py-2.5 font-mono text-[12px] uppercase tracking-wider font-semibold bg-ink text-paper hover:bg-brass transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading === 'docx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Unduh .docx
          </button>
          <button
            onClick={() => exportFile('pdf')}
            disabled={!!isLoading}
            className="px-5 py-2.5 font-mono text-[12px] uppercase tracking-wider font-semibold border-2 border-ink text-ink hover:bg-ink hover:text-paper transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Unduh .pdf
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2.5 font-mono text-[12px] uppercase tracking-wider font-semibold border border-ink-line text-ink-2 hover:border-ink hover:text-ink transition flex items-center gap-2"
            title="Print via browser (Ctrl+P)"
          >
            <FileText className="w-4 h-4" />
            Print Inline
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
            ⚠ Gagal: {error}
          </div>
        )}

        <p className="mt-4 pt-4 border-t border-ink-line font-mono text-[10px] uppercase tracking-wider text-ink-3 leading-relaxed">
          ⚠ Dokumen bersifat INDIKATIF. Verifikasi manual oleh Penyidik/JPU wajib sebelum dijadikan dasar tindakan hukum.
          Aktivitas ekspor dicatat dalam audit trail.
        </p>
      </div>
    </section>
  );
}

// ============ DOKUMEN VIEWER SECTION ============

function DokumenSection({ docId, doc }: { docId: string; doc: Document }) {
  const fileUrl = `${import.meta.env.VITE_API_URL}/api/v1/documents/${docId}/file`;
  const isPdf = (doc.mime_type || '').includes('pdf');
  // Inline auth header tidak bisa di iframe — pakai query token? Untuk dev, X-Dev-User header bypass.
  // Workaround: fetch + blob URL.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revokeUrl: string | null = null;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await fetch(fileUrl, { headers: { 'X-Dev-User': 'admin@netra.local' } });
        if (!resp.ok) throw new Error(await resp.text());
        const blob = await resp.blob();
        revokeUrl = URL.createObjectURL(blob);
        setBlobUrl(revokeUrl);
      } catch (e: any) {
        setError(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [fileUrl]);

  const downloadOriginal = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    const ext = (doc.mime_type || '').includes('pdf') ? 'pdf'
              : (doc.mime_type || '').includes('word') ? 'docx'
              : 'bin';
    a.download = `${(doc.bap_number || docId.slice(0, 8)).replace(/\//g, '_')}.${ext}`;
    a.click();
  };

  return (
    <section>
      <GenerateAllButton docId={docId} />

      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-display font-bold text-[22px] tracking-tight flex items-center gap-2">
          <FileText className="w-5 h-5 text-brass" />
          Dokumen Asli
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— ORIGINALE</span>
        </h2>
        <div className="ml-auto flex gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-3">
          <span>Type: <strong className="text-ink">{doc.mime_type || '—'}</strong></span>
          <span>· Size: <strong className="text-ink">{(doc.file_size / 1024).toFixed(1)} KB</strong></span>
          <span>· {doc.page_count || '—'} hal</span>
          <button
            onClick={downloadOriginal}
            disabled={!blobUrl}
            className="px-3 py-1 border border-brass text-brass hover:bg-brass hover:text-paper transition font-mono text-[10px] uppercase tracking-wider flex items-center gap-1 normal-case tracking-wider disabled:opacity-50"
          >
            <Download className="w-3 h-3" />
            Unduh asli
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-16 font-mono text-xs text-ink-3">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-brass" />
          Memuat dokumen dari penyimpanan...
        </div>
      )}

      {error && (
        <div className="p-4 border-2 border-crimson bg-crimson/5 font-mono text-xs text-crimson">
          ⚠ Gagal memuat dokumen: {error}
        </div>
      )}

      {blobUrl && isPdf && (
        <div className="bg-paper-2/30 border border-ink-line p-1">
          <iframe
            src={blobUrl}
            title="BAP Viewer"
            className="w-full"
            style={{ height: '78vh', border: 'none' }}
          />
        </div>
      )}

      {blobUrl && !isPdf && (
        <div className="bg-paper-2/30 border border-ink-line p-8 text-center">
          <FileText className="w-12 h-12 mx-auto mb-3 text-ink-3" />
          <p className="font-display text-[14px] mb-2">Preview inline tidak tersedia untuk tipe {doc.mime_type}</p>
          <p className="font-mono text-[11px] text-ink-2 mb-4">Klik tombol di bawah untuk mengunduh dan membuka secara lokal.</p>
          <button
            onClick={downloadOriginal}
            className="px-4 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5" />
            Unduh Dokumen Asli
          </button>
        </div>
      )}

      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-ink-3">
        Akses dokumen asli dicatat dalam audit trail (event BAP_READ).
      </p>
    </section>
  );
}

// ============ LAMPIRAN SECTION ============

interface Attachment {
  id: string;
  document_id: string;
  attachment_type: string;
  description: string | null;
  file_size: number;
  mime_type: string;
  original_filename: string | null;
  uploader_id: string | null;
  created_at: string | null;
}

const ATTACHMENT_TYPES = [
  'FOTO_TKP', 'SKETSA', 'SCREENSHOT_CHAT',
  'KWITANSI_KONTRAK', 'SURAT_PERINTAH', 'SURAT_RESMI',
  'FORENSIK_DIGITAL', 'REKAMAN_AUDIO', 'REKAMAN_VIDEO',
  'SIDIK_JARI', 'AUTOPSI', 'BAP_TAMBAHAN', 'LAINNYA',
];

const ATT_TYPE_LABEL: Record<string, string> = {
  FOTO_TKP: 'Foto TKP',
  SKETSA: 'Sketsa',
  SCREENSHOT_CHAT: 'Screenshot Chat',
  KWITANSI_KONTRAK: 'Kwitansi/Kontrak',
  SURAT_PERINTAH: 'Surat Perintah',
  SURAT_RESMI: 'Surat Resmi',
  FORENSIK_DIGITAL: 'Forensik Digital',
  REKAMAN_AUDIO: 'Rekaman Audio',
  REKAMAN_VIDEO: 'Rekaman Video',
  SIDIK_JARI: 'Sidik Jari',
  AUTOPSI: 'Autopsi',
  BAP_TAMBAHAN: 'BAP Tambahan',
  LAINNYA: 'Lainnya',
};

function LampiranSection({ docId }: { docId: string }) {
  const qc = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);

  const { data: attachments, isLoading } = useQuery<Attachment[]>({
    queryKey: ['attachments', docId],
    queryFn: () => api(`/api/v1/documents/${docId}/attachments`),
  });

  const deleteMut = useMutation({
    mutationFn: async (attId: string) => {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/documents/${docId}/attachments/${attId}`,
        { method: 'DELETE', headers: { 'X-Dev-User': 'admin@netra.local' } },
      );
      if (!resp.ok) throw new Error(await resp.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', docId] }),
  });

  // Group by type
  const grouped: Record<string, Attachment[]> = {};
  (attachments || []).forEach((a) => {
    grouped[a.attachment_type] = grouped[a.attachment_type] || [];
    grouped[a.attachment_type].push(a);
  });

  return (
    <section>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-display font-bold text-[18px] tracking-tight flex items-center gap-2">
          <Box className="w-4 h-4 text-brass" />
          Lampiran Berkas
          <span className="font-mono text-[11px] tracking-widest text-ink-3 font-normal">— ADJUNCTA</span>
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          {(attachments || []).length} lampiran
        </span>
        <button
          onClick={() => setShowUpload(true)}
          className="ml-auto px-3 h-9 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Upload Lampiran
        </button>
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat lampiran...</p>}

      {attachments && attachments.length === 0 && (
        <div className="text-center py-12 font-mono text-xs text-ink-3 border border-dashed border-ink-line">
          <Box className="w-10 h-10 mx-auto mb-2 opacity-40" />
          Belum ada lampiran. Klik "Upload Lampiran" untuk tambah foto/sketsa/surat/bukti.
        </div>
      )}

      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass font-bold mb-2 border-b border-ink-line pb-1">
            {ATT_TYPE_LABEL[type] || type} ({items.length})
          </div>
          <div className="grid grid-cols-3 gap-3">
            {items.map((a) => (
              <AttachmentCard
                key={a.id}
                docId={docId}
                att={a}
                onDelete={() => {
                  if (confirm(`Hapus lampiran "${a.original_filename}"?`)) deleteMut.mutate(a.id);
                }}
              />
            ))}
          </div>
        </div>
      ))}

      {showUpload && (
        <AttachmentUploadModal
          docId={docId}
          onClose={() => setShowUpload(false)}
          onSaved={() => {
            setShowUpload(false);
            qc.invalidateQueries({ queryKey: ['attachments', docId] });
          }}
        />
      )}
    </section>
  );
}

function AttachmentCard({ docId, att, onDelete }: { docId: string; att: Attachment; onDelete: () => void }) {
  const fileUrl = `${import.meta.env.VITE_API_URL}/api/v1/documents/${docId}/attachments/${att.id}/file`;
  const isImage = (att.mime_type || '').startsWith('image/');
  const isPdf = (att.mime_type || '').includes('pdf');
  const [imgBlob, setImgBlob] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return;
    let url: string | null = null;
    fetch(fileUrl, { headers: { 'X-Dev-User': 'admin@netra.local' } })
      .then((r) => r.blob())
      .then((b) => { url = URL.createObjectURL(b); setImgBlob(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileUrl, isImage]);

  const download = async () => {
    const resp = await fetch(fileUrl, { headers: { 'X-Dev-User': 'admin@netra.local' } });
    const blob = await resp.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = att.original_filename || `attachment-${att.id}`;
    a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="bg-paper-2/40 border border-ink-line overflow-hidden">
      <div className="aspect-[4/3] bg-paper-3/30 grid place-items-center overflow-hidden">
        {isImage && imgBlob ? (
          <img src={imgBlob} alt={att.original_filename || ''} className="object-cover w-full h-full" />
        ) : isPdf ? (
          <FileText className="w-12 h-12 text-crimson opacity-60" />
        ) : (
          <Box className="w-12 h-12 text-ink-3 opacity-60" />
        )}
      </div>
      <div className="p-3">
        <div className="font-mono text-[11px] text-ink font-bold truncate" title={att.original_filename || ''}>
          {att.original_filename || att.id.slice(0, 8)}
        </div>
        <div className="font-mono text-[9px] text-ink-3 mt-0.5">
          {(att.file_size / 1024).toFixed(1)} KB · {att.mime_type}
        </div>
        {att.description && (
          <p className="font-serif text-[12px] text-ink-2 mt-2 line-clamp-2">{att.description}</p>
        )}
        <div className="mt-2 flex gap-1">
          <button
            onClick={download}
            className="flex-1 px-2 py-1 border border-brass text-brass hover:bg-brass hover:text-paper font-mono text-[9px] uppercase tracking-wider flex items-center justify-center gap-1"
          >
            <Download className="w-2.5 h-2.5" />
            Unduh
          </button>
          <button
            onClick={onDelete}
            className="px-2 py-1 border border-ink-line text-ink-2 hover:border-crimson hover:text-crimson font-mono text-[9px] uppercase tracking-wider"
            title="Hapus"
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AttachmentUploadModal({ docId, onClose, onSaved }: { docId: string; onClose: () => void; onSaved: () => void }) {
  const [attType, setAttType] = useState('FOTO_TKP');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pilih file dulu');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('attachment_type', attType);
      if (description) fd.append('description', description);
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/documents/${docId}/attachments`,
        {
          method: 'POST',
          headers: { 'X-Dev-User': 'admin@netra.local' },
          body: fd,
        },
      );
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(String(e?.message || e)),
  });

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-8" style={{ background: 'rgba(0,0,0,0.78)' }} onClick={onClose}>
      <div className="bg-paper border-2 border-ink max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b-2 border-ink bg-paper-2/40 flex items-center gap-3">
          <Box className="w-4 h-4 text-brass" />
          <h3 className="font-display font-bold text-[16px]">Upload Lampiran</h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); uploadMut.mutate(); }} className="p-5 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1.5">Jenis Lampiran</label>
            <select value={attType} onChange={(e) => setAttType(e.target.value)} className="w-full px-3 py-2 border border-ink-line bg-paper font-mono text-[12px] focus:border-brass outline-none">
              {ATTACHMENT_TYPES.map((t) => (
                <option key={t} value={t}>{ATT_TYPE_LABEL[t] || t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1.5">Deskripsi (opsional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 border border-ink-line bg-paper font-serif text-[13px] focus:border-brass outline-none resize-none" placeholder="Lokasi/tanggal/keterangan singkat..." />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1.5">File (max 100 MB)</label>
            <div
              onClick={() => fileRef.current?.click()}
              className="p-6 border-2 border-dashed border-ink-line hover:border-brass cursor-pointer text-center transition"
            >
              <input
                ref={fileRef}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
              {file ? (
                <>
                  <FileText className="w-8 h-8 mx-auto mb-2 text-brass" />
                  <div className="font-mono text-[12px] text-ink">{file.name}</div>
                  <div className="font-mono text-[10px] text-ink-3 mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type || '—'}</div>
                </>
              ) : (
                <>
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-ink-3 opacity-60" />
                  <div className="font-mono text-[11px] text-ink-2">Klik untuk pilih file</div>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 border-2 border-crimson bg-crimson/5 font-mono text-[10px] text-crimson">⚠ {error}</div>
          )}

          <div className="flex gap-2 pt-4 border-t border-dashed border-ink-line">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-ink-line text-ink-2 hover:border-ink font-mono text-[11px] uppercase tracking-wider">
              Batal
            </button>
            <button
              type="submit"
              disabled={!file || uploadMut.isPending}
              className="ml-auto px-5 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {uploadMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ GENERATE SEMUA ANALISA (one-click) ============

// Job kinds submitted in parallel for the one-click pipeline. Each kind
// goes through the backend dedupe window so repeated clicks of the same
// button collapse to a single set of running jobs.
const AI_KINDS_FOR_ALL: { kind: import('@/shared/jobs').JobKind; params?: Record<string, unknown> }[] = [
  { kind: 'summary', params: { level: 'executive' } },
  { kind: 'findings', params: { force: true } },
  { kind: 'violation', params: { force: true } },
  { kind: 'inconsistency', params: { force: true } },
  { kind: 'timeline', params: { force: true } },
  { kind: 'entity', params: { force: true } },
  { kind: 'pii_llm', params: { force: true } },
];

function GenerateAllButton({ docId }: { docId: string }) {
  const { submit, active } = useJobs();
  const { data: counts } = useQuery<Counts>({
    queryKey: ['counts', docId],
    queryFn: () => api(`/api/v1/documents/${docId}/counts`),
    refetchInterval: 10000,
  });

  const hasAny =
    !!counts &&
    counts.findings + counts.violations + counts.inconsistencies + counts.timeline + counts.entities + counts.pii_total > 0;

  // Submit-disable when any AI job for this doc is in flight.
  const anyRunningHere = active.some((j) => j.doc_id === docId);

  const handleClick = async () => {
    // Fire all submits in parallel — backend dedupes (user+doc+kind+params)
    // so a stuck click while jobs run produces no duplicates.
    await Promise.all(
      AI_KINDS_FOR_ALL.map(({ kind, params }) =>
        submit(kind, docId, params || {}).catch((e) => {
          console.warn(`[generate-all] submit ${kind} failed`, e);
        }),
      ),
    );
  };

  return (
    <div className="mb-5 p-4 border-2 border-brass bg-brass-soft/40 flex items-center gap-4">
      <Sparkles className="w-7 h-7 text-brass shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-[15px] tracking-tight">Analisa Cepat AI — One-Click</div>
        <p className="font-mono text-[11px] text-ink-2 mt-1 leading-relaxed">
          Jalankan ringkasan + temuan + pasal + kontradiksi + kronologi + entitas + PII sekaligus.
          {hasAny && (
            <span className="text-ink-2"> Idempoten — klik ulang tidak duplikat (dedupe 5 menit).</span>
          )}
        </p>
      </div>
      <button
        onClick={handleClick}
        disabled={anyRunningHere}
        title={anyRunningHere ? 'Ada job AI yang sedang berjalan untuk BAP ini' : ''}
        className="px-5 py-2.5 font-mono text-[12px] uppercase tracking-wider font-bold bg-ink text-paper hover:bg-brass transition flex items-center gap-2 disabled:opacity-50 shrink-0"
      >
        {anyRunningHere ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {hasAny ? 'Regenerate Semua' : 'Generate Semua'}
      </button>
    </div>
  );
}
